import AssignCasesAutocomplete from "@/components/Forms/AssignCasesAutocomplete.tsx";
import AssignCasesDisplay from "@/components/Forms/AssignCasesDisplay.tsx";
import CaseTemplatesAutocomplete from "@/components/Forms/CaseTemplateAutocomplete.tsx";
import { addTasksToCasesByCaseTemplateSchema } from "@/components/Forms/schema/addCaseTasksSchema.ts";
import { useAddCaseTask } from "@/mutations/useAddCaseTask.tsx";
import { useFetchCase } from "@/queries/useFetchCase.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2 as Grid } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useEffect } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddTaskFormValues = z.infer<typeof addTasksToCasesByCaseTemplateSchema>;

type Props = {
    handleClose: () => void;
    foundCaseId?: string;
};

const AssignTasksByTemplateForm = ({ handleClose, foundCaseId }: Props): ReactNode => {
    const addTask = useAddCaseTask();

    const {
        data: { data: caseData } = {},
    } = useFetchCase(foundCaseId);

    const form = useForm<AddTaskFormValues>({
        resolver: zodResolver(addTasksToCasesByCaseTemplateSchema),
        defaultValues: {
            cases: [],
        },
    });

    const handleSubmit = (values: AddTaskFormValues) => {
        addTask
            .mutateAsync({
                ...values,
            })
            .then(() => {
                enqueueSnackbar("Tasks added to case(s).", { variant: "success" });
                form.reset({});
                handleClose();
            })
            .catch(() => {
                enqueueSnackbar("Error: Add Task Template failed.", { variant: "error" });
            });
    };

    const onSubmit: SubmitHandler<AddTaskFormValues> = async (
        data: AddTaskFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    useEffect(() => {
        if (caseData) {
            form.setValue("cases", [
                { id: caseData.idUUID, name: caseData.clientName ?? "Unknown" },
            ]);
        }
    }, [caseData]);

    return (
        <FormProvider {...form}>
            <form>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesAutocomplete />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1, py: 2 }}>
                        <AssignCasesDisplay />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ pt: 1, py: 2 }}>
                        <CaseTemplatesAutocomplete />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ pt: 1, textAlign: "center" }}>
                        <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                            Assign Tasks
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </FormProvider>
    );
};

export default AssignTasksByTemplateForm;
